import { PlaceholderEditorVariant, PlaceholderListValue } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  useCreateGoalBankGoalsMutation,
  useCreateGoalBankTargetsMutation,
} from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

export enum Goals {
  SHORT_TERM_GOALS = "Short Term Goals",
  TARGETS = "Targets",
}
export const GoalBankGoalsFormConfig = {
  [Goals.SHORT_TERM_GOALS]: {
    title: "New Short Term Goal",
    mutation: useCreateGoalBankGoalsMutation,
    defaultValues: {
      folderId: "",
      shorttermGoalsData: [
        {
          goalName: "",
          description: "",
          medicalRationale: "",
          programGoal: "",
          masteryCriteria: "",
          assessmentToolSource: "",
          timelineEstimationType: null,
          timelineEstimationValue: null,
        },
      ],
    },
  },
  [Goals.TARGETS]: {
    title: "New Target",
    mutation: useCreateGoalBankTargetsMutation,
    defaultValues: {
      folderId: "",
      goalBankTargetData: [
        {
          goalName: "",
          description: "",
          medicalRationale: "",
          programGoal: "",
          masteryCriteria: "",
          timelineEstimationType: null,
          timelineEstimationValue: null,
        },
      ],
    },
  },
};
export enum FolderManagementAction {
  MOVE,
  SAVE,
}

export interface ExtendedPlaceholderListValue extends PlaceholderListValue {
  key: string;
}

export const RecommendationPlaceholderEditorVariant = PlaceholderEditorVariant.WYSIWYG;

export const PlaceholderValues: ExtendedPlaceholderListValue[] = [
  // Client Information
  { id: 1, value: "Client Full Name", icon: "user", key: "clientFullName" },
  { id: 2, value: "Client First Name", icon: "user", key: "clientFirstName" },
  { id: 3, value: "Client Last Name", icon: "user", key: "clientLastName" },
  { id: 4, value: "Date of Birth", icon: "calendar", key: "dateOfBirth" },
  { id: 5, value: "Age", icon: "calendar", key: "age" },
  { id: 6, value: "Gender", icon: "user", key: "gender" },
  { id: 7, value: "Address", icon: "map-pin", key: "address" },

  // Primary Guardian/Caregiver Information
  { id: 8, value: "Guardian Full Name", icon: "users", key: "guardianFullName" },
  { id: 9, value: "Guardian First Name", icon: "users", key: "guardianFirstName" },
  { id: 10, value: "Guardian Last Name", icon: "users", key: "guardianLastName" },
  { id: 11, value: "Guardian Phone", icon: "phone", key: "guardianPhone" },

  // Secondary Guardian/Caregiver Information
  { id: 21, value: "Guardian 2 Full Name", icon: "users", key: "guardian2FullName" },
  { id: 22, value: "Guardian 2 First Name", icon: "users", key: "guardian2FirstName" },
  { id: 23, value: "Guardian 2 Last Name", icon: "users", key: "guardian2LastName" },
  { id: 24, value: "Guardian 2 Phone", icon: "phone", key: "guardian2Phone" },

  // Report Information
  { id: 12, value: "Email", icon: "mail", key: "email" },
  { id: 13, value: "Date of Report", icon: "calendar", key: "dateOfReport" },
  { id: 14, value: "Signed By", icon: "pen-tool", key: "signedBy" },
  { id: 15, value: "Professional Title", icon: "briefcase", key: "professionalTitle" },
  { id: 16, value: "Practitioner Email", icon: "mail", key: "practitionerEmail" },
  { id: 17, value: "Report Writer", icon: "edit", key: "reportWriter" },
  { id: 18, value: "Date and Time of Assessment", icon: "calendar", key: "dateAndTimeOfAssessment" },
  { id: 19, value: "License Number", icon: "user", key: "signedByLicenseNumber" },

  // Organization Information
  { id: 20, value: "Organization Name", icon: "briefcase", key: "organizationName" },

  // Insurance Information
  { id: 25, value: "Insurer", icon: "briefcase", key: "insurer" },
  { id: 26, value: "Insurance ID", icon: "briefcase", key: "insuranceId" },
  { id: 27, value: "Authorization Start", icon: "calendar", key: "authorizationStart" },
  { id: 28, value: "Authorization End", icon: "calendar", key: "authorizationEnd" },
];

// Create mappings for conversion
const PLACEHOLDER_DISPLAY_TO_KEY = new Map<string, string>(PlaceholderValues.map((p) => [p.value, p.key]));

const PLACEHOLDER_KEY_TO_DISPLAY = new Map<string, string>(PlaceholderValues.map((p) => [p.key, p.value]));

export function convertDisplayPlaceholdersToKeys(text: string): string {
  if (!text) return text;

  // Remove any zero-width non-breaking space characters that might be introduced during editing
  let result = text.replace(/\uFEFF/g, "");
  PLACEHOLDER_DISPLAY_TO_KEY.forEach((key, displayName) => {
    const displayPlaceholder = `{${displayName}}`;
    const keyPlaceholder = `{${key}}`;
    // Make the replacement case-insensitive
    result = result.replace(new RegExp(escapeRegExp(displayPlaceholder), "gi"), keyPlaceholder);
  });

  // Remove any zero-width non-breaking space characters that might be introduced during editing
  return result.replace(/\uFEFF/g, "");
}

export function convertKeyPlaceholdersToDisplay(text: string): string {
  if (!text) return text;

  let result = text;

  // First, handle keys that exist in the mapping
  PLACEHOLDER_KEY_TO_DISPLAY.forEach((displayName, key) => {
    const keyPlaceholder = `{${key}}`;
    const displayPlaceholder = `{${displayName}}`;
    result = result.replace(new RegExp(escapeRegExp(keyPlaceholder), "g"), displayPlaceholder);
  });

  // Then, handle any remaining keys that weren't in the mapping (id_number, insurer etc)
  result = result.replace(/\{([^}]+)\}/g, (match, key) => {
    if (!PLACEHOLDER_KEY_TO_DISPLAY.has(key)) {
      const readableString = convertReadableString(key);
      return `{${readableString}}`;
    }
    return match;
  });

  return result;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export const ReportTypeRedirectMap = new Map<ApiReportReportTypeChoices, string>([
  [ApiReportReportTypeChoices.Reassessment, "reassessment"],
  [ApiReportReportTypeChoices.InitialAssessment, "assessment-reports"],
  [ApiReportReportTypeChoices.DiagnosisEvaluation, "evaluations"],
]);
